import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { migrateGuestData } from "@/lib/auth/profile.functions";
import { readPersistedAttribution } from "@/lib/attribution/attributionEngine";

const LEGACY_FLAG = "ss2026_registered";
const LOGOUT_FLAG = "vs:logout_in_progress";
export const AUTH_FORCE_SIGNED_OUT_EVENT = "vs:auth:forced-signout";

function authStorageKeys(): string[] {
  const ref = (import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined) ?? "";
  return [
    ref ? `sb-${ref}-auth-token` : "",
    ref ? `sb-${ref}-code-verifier` : "",
    "supabase.auth.token",
  ].filter(Boolean);
}

function markLogoutInProgress() {
  try {
    window.localStorage.setItem(LOGOUT_FLAG, String(Date.now()));
  } catch { /* noop */ }
}

export function isLogoutInProgress(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(LOGOUT_FLAG);
    const ts = raw ? Number(raw) : 0;
    return Number.isFinite(ts) && Date.now() - ts < 15_000;
  } catch {
    return false;
  }
}

function purgeLocalAuthSession() {
  if (typeof window === "undefined") return;
  try {
    for (const key of authStorageKeys()) {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
    }
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && /^sb-.+-auth-token$/.test(key)) window.localStorage.removeItem(key);
    }
    window.localStorage.removeItem("ss2026_session_id");
    window.dispatchEvent(new CustomEvent(AUTH_FORCE_SIGNED_OUT_EVENT));
  } catch { /* noop */ }
}

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let id: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        id = setTimeout(() => reject(new Error(label)), ms);
      }),
    ]);
  } finally {
    if (id) clearTimeout(id);
  }
}

function setLegacyFlag(on: boolean) {
  try {
    if (on) window.localStorage.setItem(LEGACY_FLAG, "true");
    else window.localStorage.removeItem(LEGACY_FLAG);
    window.dispatchEvent(new CustomEvent("ss2026:registered"));
  } catch { /* noop */ }
}

function callbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

function readGuestState() {
  try {
    const language = window.localStorage.getItem("vs:lang") ?? undefined;
    const favorite_team = window.localStorage.getItem("vs:team") ?? undefined;
    const palette = window.localStorage.getItem("vs:palette") ?? undefined;
    const onbRaw = window.localStorage.getItem("vs:onboarding") ?? null;
    const onboarding_progress = onbRaw ? safeJson(onbRaw) : undefined;
    const attr = readPersistedAttribution();
    return {
      language: language ?? undefined,
      favorite_team: favorite_team ?? undefined,
      palette: palette ?? undefined,
      onboarding_progress,
      attribution: attr ? (attr as unknown as Record<string, unknown>) : undefined,
    };
  } catch {
    return {};
  }
}

function safeJson(s: string): Record<string, unknown> | undefined {
  try {
    const v = JSON.parse(s);
    return v && typeof v === "object" ? (v as Record<string, unknown>) : undefined;
  } catch { return undefined; }
}

export async function signUpEmail(opts: {
  email: string;
  password: string;
  display_name: string;
  language: "ES" | "EN" | "PT";
  favorite_team?: string;
}) {
  // P0-AUTH-01 — Hard-reset any pre-existing session BEFORE signUp.
  // Without this, a stale localStorage session from a previous user causes
  // the new account to be orphaned: NationGate/verify-email read the old
  // session and persist the new user's selections onto the wrong profile.
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      await supabase.auth.signOut();
    }
  } catch (e) {
    console.warn("[signUpEmail] pre-signout failed", e);
  }
  try {
    window.localStorage.setItem("vs:pending_signup_email", opts.email.trim().toLowerCase());
  } catch { /* noop */ }
  const { data, error } = await supabase.auth.signUp({
    email: opts.email,
    password: opts.password,
    options: {
      emailRedirectTo: callbackUrl(),
      data: {
        display_name: opts.display_name,
        language: opts.language,
        favorite_team: opts.favorite_team ?? null,
      },
    },
  });
  if (error) throw error;
  setLegacyFlag(true);
  return data;
}

export async function signInEmail(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  setLegacyFlag(true);
  return data;
}

export async function signInGoogle() {
  return lovable.auth.signInWithOAuth("google", {
    redirect_uri: callbackUrl(),
  });
}

export async function signInApple() {
  return lovable.auth.signInWithOAuth("apple", {
    redirect_uri: callbackUrl(),
  });
}

export async function signOut() {
  console.log("[AUTH_SIGNOUT_START]");
  markLogoutInProgress();
  setLegacyFlag(false);
  try {
    await withTimeout(supabase.auth.signOut(), 2000, "signout_timeout");
    console.log("[AUTH_SIGNOUT_OK]");
  } catch (e) {
    console.warn("[AUTH_SIGNOUT_ERR]", e);
  } finally {
    purgeLocalAuthSession();
    console.log("[AUTH_SIGNOUT_LOCAL_PURGED]");
  }
}

export async function resetPassword(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
}

export async function resendVerification(email: string) {
  const { error } = await supabase.auth.resend({
    type: "signup",
    email,
    options: { emailRedirectTo: callbackUrl() },
  });
  if (error) throw error;
}

/** Push localStorage guest state into the authenticated user's profile. */
export async function migrateGuestStateToProfile() {
  const state = readGuestState();
  if (!state || Object.keys(state).length === 0) return;
  try {
    await migrateGuestData({ data: state });
  } catch (e) {
    console.warn("[migrateGuestStateToProfile]", e);
  }
}